import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as common from '../../common'

import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import { Term } from './term'
import { Type } from './type'

export interface Structure<TId extends Structure.Id = Structure.Id>
  extends Model<TId> {
  // TODO:
  // struct: common.value.types.Structure
}
export namespace Structure {
  export const isStructure: TypeChecker<Structure> = TypeChecker.Factory.lazy(
    () => Model.getTypeChecker(Id.isId),
  ).usingAlias('Structure')
  /**
   * Just checks if the object's ID indicates that it is a purpose. This can be
   * used to distinguish between various model types without checking that every
   * field of the model is correct.
   */
  export const shouldBeStructure = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(Type.Structure),
  )

  export interface Input extends Omit<Structure, 'id' | 'terms'> {
    partialId?: PartialId.Standard
    termIds?: readonly Term.Id[]
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isStructure, ['id']),
        TypeChecker.Factory.object<Difference<Input, Structure>>({
          partialId: TypeChecker.Factory.optional(
            PartialId.Standard.isStandard,
          ),
          termIds: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Term.Id.isId),
          ),
        }),
      ),
    ).usingAlias('Structure.Input')
  }

  export type Updates = Partial<Omit<Input, 'partialId'>>
  export namespace Updates {
    export const isUpdates: TypeChecker<Updates> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          TypeChecker.Factory.omit(isStructure, ['id']),
          TypeChecker.Factory.object<Difference<Updates, Input>>({
            name: TypeChecker.Factory.optional(TypeChecker.isString),
          }),
        ),
    ).usingAlias('Structure.Updates')
  }

  export type Identifiable<TPartialId extends PartialId = PartialId> =
    _Identifiable.ForSpecificType<Type.Structure, TPartialId, Id.Context>
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        _Identifiable.ForSpecificType.getTypeChecker(
          Type.Structure,
          Id.Context.isContext,
        ),
      ).usingAlias('dms.models.Structure.Identifiable')
  }

  export type Id<TPartialId extends PartialId = PartialId> = _Id<
    Type.Structure,
    TPartialId,
    Id.Context
  >
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(Type.Structure, Context.isContext),
    ).usingAlias('Structure.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId>
        ? Id<TPartialId>
        : never

    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(source: Identifiable): Id {
      return _Id.from<Type.Structure, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, Type.Structure),
      )
    }

    export type Context = _Id.Context.None
    export namespace Context {
      export const isContext: TypeChecker<Context> =
        _Id.Context.None.isNone.usingAlias('Structure.Id.Context')
    }
  }
}
